import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  FileUploadStatusItem,
  fileUploadStatusCollapsedAtom,
  fileUploadStatusItemsAtom,
  fileUploadStatusVisibleAtom,
} from '@/recoil/atom/file-upload-status';
import isInApp from '@/utils/approval/isInApp';
import {
  CloseButton,
  ErrorIcon,
  Header,
  HeaderIconButton,
  HeaderTitle,
  ItemMessage,
  ItemMeta,
  ItemName,
  ItemRow,
  ItemsScroll,
  ModalRoot,
  SpinnerRing,
  StatusIconBox,
  SuccessIcon,
  ThumbBox,
} from './styles';

const IMG_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp']);

const getExt = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx < 0 ? '' : name.slice(idx + 1).toLowerCase();
};

/** 확장자 → 아이콘 파일명 매핑. AttachmentList(AttachmentItem)와 동일 규칙 사용. */
const iconForExt = (ext: string): string => {
  if (IMG_EXTS.has(ext)) return '/icons/approval/attachment-img.svg';
  if (ext === 'doc' || ext === 'docx')
    return '/icons/approval/attachment-docx.svg';
  if (ext === 'pdf') return '/icons/approval/attachment-pdf.svg';
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv')
    return '/icons/approval/attachment-xlsx.svg';
  if (ext === 'ppt' || ext === 'pptx')
    return '/icons/approval/attachment-ppt.svg';
  if (ext === 'hwp' || ext === 'hwpx')
    return '/icons/approval/attachment-hwp.svg';
  if (ext === 'txt') return '/icons/approval/attachment-txt.svg';
  if (
    ext === 'zip' ||
    ext === 'rar' ||
    ext === '7z' ||
    ext === 'tar' ||
    ext === 'gz'
  )
    return '/icons/approval/attachment-zip.svg';
  return '/icons/approval/attachment-file.svg';
};

const renderStatusIcon = (
  status: FileUploadStatusItem['status'],
  t: (key: string) => string,
) => {
  if (status === 'uploading')
    return <SpinnerRing aria-label={t('업로드 중')} />;
  if (status === 'success') {
    return <SuccessIcon role="img" aria-label={t('업로드 성공')} />;
  }
  return <ErrorIcon role="img" aria-label={t('업로드 실패')} />;
};

const renderThumb = (item: FileUploadStatusItem) => {
  if (item.previewUrl) {
    return <img className="preview" src={item.previewUrl} alt="" />;
  }
  return (
    <img className="icon" src={iconForExt(getExt(item.fileName))} alt="" />
  );
};

const FileUploadStatusModalView = () => {
  const { t } = useTranslation();
  const items = useAtomValue(fileUploadStatusItemsAtom);
  const [visible, setVisible] = useAtom(fileUploadStatusVisibleAtom);
  const [collapsed, setCollapsed] = useAtom(fileUploadStatusCollapsedAtom);

  // unmount 시 남아있는 objectURL을 일괄 revoke. ref로 최신 items 추적 (cleanup 클로저가 stale items를 잡지 않도록).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    return () => {
      itemsRef.current.forEach((it) => {
        if (it.previewUrl && typeof URL.revokeObjectURL === 'function') {
          try {
            URL.revokeObjectURL(it.previewUrl);
          } catch {
            // noop
          }
        }
      });
    };
  }, []);

  const { uploadingCount, successCount } = useMemo(() => {
    let uploading = 0;
    let success = 0;
    for (const it of items) {
      if (it.status === 'uploading') uploading += 1;
      else if (it.status === 'success') success += 1;
    }
    return { uploadingCount: uploading, successCount: success };
  }, [items]);

  if (!visible || items.length === 0) return null;

  const headerTitle =
    uploadingCount > 0
      ? t('항목 {{n}}개 업로드 중', { n: uploadingCount })
      : t('항목 {{n}}개 업로드 완료', { n: successCount });

  const handleToggle = () => setCollapsed((prev) => !prev);

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // 닫기는 모달만 hide. items는 보존(스펙: removeItem 시에만 정리).
    setVisible(false);
    // 다음 addUploading 때 펼친 상태로 다시 보이도록 collapsed 리셋
    setCollapsed(false);
  };

  return (
    <ModalRoot role="region" aria-label={t('파일 업로드 상태')}>
      <Header
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-expanded={!collapsed}
        aria-controls="file-upload-status-list"
      >
        <HeaderIconButton aria-hidden="true">
          <img
            src={
              collapsed
                ? '/icons/approval/chevron-up.svg'
                : '/icons/approval/chevron-down.svg'
            }
            alt=""
          />
        </HeaderIconButton>
        <HeaderTitle as="span">{headerTitle}</HeaderTitle>
        <CloseButton
          type="button"
          onClick={handleClose}
          aria-label={t('업로드 상태 모달 닫기')}
        >
          <img src="/icons/approval/close.svg" alt="" />
        </CloseButton>
      </Header>
      {!collapsed && (
        <ItemsScroll id="file-upload-status-list" role="log">
          {items.map((item) => (
            <ItemRow key={item.id}>
              <ThumbBox>{renderThumb(item)}</ThumbBox>
              <ItemMeta>
                <ItemName as="span">{item.fileName}</ItemName>
                {item.message ? (
                  <ItemMessage
                    as="span"
                    tone={item.status === 'error' ? 'error' : 'muted'}
                  >
                    {item.message}
                  </ItemMessage>
                ) : null}
              </ItemMeta>
              <StatusIconBox>{renderStatusIcon(item.status, t)}</StatusIconBox>
            </ItemRow>
          ))}
        </ItemsScroll>
      )}
    </ModalRoot>
  );
};

/**
 * 글로벌 파일 업로드 상태 모달 — _app 또는 layout 최상위에 1회만 마운트.
 * - 호스트 앱 모드(portalApiKey/RNWebview)에서는 호스트가 처리하므로 렌더링 자체를 스킵
 * - SSR 환경 보호: window 접근 후 마운트 여부 결정
 */
const FileUploadStatusModal = () => {
  const [isClient, setIsClient] = useState(false);
  const [hostHandled, setHostHandled] = useState(false);

  useEffect(() => {
    setIsClient(true);
    setHostHandled(isInApp());
  }, []);

  if (!isClient) return null;
  if (hostHandled) return null;

  return <FileUploadStatusModalView />;
};

export default FileUploadStatusModal;
